import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

// Import direct des logos depuis assets
import logoHorizontal from "../../assets/Logo MIMAP Horizontal second--02.png";
// import { CLINIC_INFO } from "../utils/preEmploymentUtils";

// Définition des styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },
  container: {
    flex: 1,
    flexDirection: "column",
    justifyContent: "space-between",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10, // Réduit pour rapprocher la bordure
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#113285",
    borderBottomStyle: "solid",
  },
  logo: {
    width: 200,
    height: 70,
  },
  headerText: {
    textAlign: "right",
  },
  title: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#113285",
    marginBottom: 5,
  },
  receiptContainer: {
    marginTop: 5, // Espace après la bordure
    marginBottom: 15, // Espace avant la section suivante
    alignItems: "center", // Centrage horizontal
  },
  subtitle: {
    fontSize: 12, // Taille augmentée pour plus de visibilité
    color: "#113285",
    fontWeight: "bold",
    marginBottom: 3,
  },
  receiptNumber: {
    fontSize: 10,
    color: "#6b7280",
    fontWeight: "bold",
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#113285",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    borderBottomStyle: "solid",
    paddingBottom: 3,
  },
  row: {
    flexDirection: "row",
    marginBottom: 5,
    alignItems: "flex-start",
  },
  label: {
    width: "35%",
    fontWeight: "bold",
    color: "#111827",
  },
  value: {
    width: "65%",
    color: "#374151",
  },
  financialContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 15,
    gap: 10,
  },
  financialBox: {
    width: "32%",
    padding: 8,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 4,
  },
  financialTitle: {
    fontSize: 8,
    marginBottom: 4,
    color: "#6b7280",
  },
  financialValue: {
    fontSize: 12,
    fontWeight: "bold",
  },
  notes: {
    marginTop: 10,
    padding: 10,
    backgroundColor: "#f9fafb",
    borderLeftWidth: 3,
    borderLeftColor: "#113285",
    fontSize: 9,
  },
  footer: {
    marginTop: 20,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    borderTopStyle: "solid",
    textAlign: "center",
    fontSize: 8,
    color: "#6b7280",
  },
  watermark: {
    position: "absolute",
    opacity: 0.1,
    width: 300,
    height: 300,
    textAlign: "center",
    left: 30,
    top: 200,
  },
});

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const generateReceiptNumber = (dateVisit: string, id: number) => {
  const date = new Date(dateVisit);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `RECU-${year}${month}${day}-${id}`;
};

interface PatientPDFProps {
  patientInfo: {
    id: number;
    fullName: string;
    dateOfBirth: string;
    dateVisit: string;
    motif: string;
    doctorName: string;
    montant: number;
    sommePaye: number;
    sommeImpaye: number;
    telephone?: string;
    adresse?: string;
    notes?: string;
  };
}

const PatientPDF: React.FC<PatientPDFProps> = ({ patientInfo }) => {
  const receiptNumber = generateReceiptNumber(
    patientInfo.dateVisit,
    patientInfo.id
  );

  return (
    <Document>
      <Page size="A5" style={styles.page}>
        <View style={styles.container}>
          {/* Header avec logo et titre */}
          <View style={styles.header}>
            <Image style={styles.logo} src={logoHorizontal} />
            <View style={styles.headerText}>
              <Text style={styles.title}>Votre santé, notre priorité</Text>
            </View>
          </View>

          {/* Section Reçu de paiement centrée */}
          <View style={styles.receiptContainer}>
            <Text style={styles.subtitle}>REÇU DE PAIEMENT</Text>
            <Text style={styles.receiptNumber}>N° {receiptNumber}</Text>
          </View>

          {/* Informations personnelles */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>INFORMATIONS PATIENT</Text>
            <View style={styles.row}>
              <Text style={styles.label}>Nom complet:</Text>
              <Text style={styles.value}>{patientInfo.fullName}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Date de naissance:</Text>
              <Text style={styles.value}>
                {formatDate(patientInfo.dateOfBirth)}
              </Text>
            </View>
            {patientInfo.telephone && (
              <View style={styles.row}>
                <Text style={styles.label}>Téléphone:</Text>
                <Text style={styles.value}>{patientInfo.telephone}</Text>
              </View>
            )}
            {patientInfo.adresse && (
              <View style={styles.row}>
                <Text style={styles.label}>Adresse:</Text>
                <Text style={styles.value}>{patientInfo.adresse}</Text>
              </View>
            )}
          </View>

          {/* Détails consultation */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>DÉTAILS CONSULTATION</Text>
            <View style={styles.row}>
              <Text style={styles.label}>Date de visite:</Text>
              <Text style={styles.value}>
                {formatDate(patientInfo.dateVisit)}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Motif:</Text>
              <Text style={styles.value}>{patientInfo.motif}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Médecin:</Text>
              <Text style={styles.value}>{patientInfo.doctorName}</Text>
            </View>
          </View>

          {/* Financier */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>INFORMATIONS FINANCIÈRES</Text>
            <View style={styles.financialContainer}>
              <View style={styles.financialBox}>
                <Text style={styles.financialTitle}>MONTANT TOTAL</Text>
                <Text style={styles.financialValue}>
                  {patientInfo.montant} MRU
                </Text>
              </View>
              <View
                style={[styles.financialBox, { backgroundColor: "#f0fdf4" }]}
              >
                <Text style={styles.financialTitle}>MONTANT PAYÉ</Text>
                <Text style={[styles.financialValue, { color: "#16a34a" }]}>
                  {patientInfo.sommePaye} MRU
                </Text>
              </View>
              <View
                style={[
                  styles.financialBox,
                  {
                    backgroundColor:
                      patientInfo.sommeImpaye > 0 ? "#fef2f2" : "#f8fafc",
                  },
                ]}
              >
                <Text style={styles.financialTitle}>RESTE À PAYER</Text>
                <Text
                  style={[
                    styles.financialValue,
                    {
                      color:
                        patientInfo.sommeImpaye > 0 ? "#dc2626" : "#64748b",
                    },
                  ]}
                >
                  {patientInfo.sommeImpaye} MRU
                </Text>
              </View>
            </View>
          </View>

          {/* Notes */}
          {patientInfo.notes && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>NOTES</Text>
              <View style={styles.notes}>
                <Text>{patientInfo.notes}</Text>
              </View>
            </View>
          )}

          {/* Footer */}
          <View style={styles.footer}>
            <Text>Ilot k, Ext 929A Cite Plage -- Nouakchott</Text>
            <Text>Tel: 00-222-31-24-44-04 • Email: cabinetmimap@gmail.com</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PatientPDF;
